/*
 * ThemeContext — Phase 0 (Apr 28, 2026)
 *
 * Manages the user's theme preference: 'light' | 'dark' | 'system'.
 * - 'system' resolves dynamically from prefers-color-scheme media query.
 * - On first visit, defaults to 'system' (per Q4 brief: respect OS preference).
 * - User choice persists to localStorage permanently.
 * - Future Phase 1 work can sync to user.bodyProfile so preference follows account across devices.
 *
 * Visual changes are NOT in this Phase 0 — both 'light' and 'dark' currently
 * render the existing Schedule B palette via tokens in index.css. Phase 1
 * re-skins authenticated pages to consume the tokens.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'aidb_theme_preference';
const VALID_PREFERENCES = ['light', 'dark', 'system'];

const ThemeContext = createContext({
  preference: 'system',
  resolved: 'dark',
  setPreference: () => {},
  toggle: () => {},
});

function readStoredPreference() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID_PREFERENCES.includes(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

function detectSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

// ─────────────────────────────────────────────────────────────────────────────
// PARETO PAUSE — Apr 30, 2026
// Phase 1 (inner-card token conversion) is incomplete. Until inner Dashboard /
// Profile / AskAI / Health Hub cards are tokenised, "Warm" mode renders washed
// out (dark cards on cream bg). To avoid shipping a broken state we:
//   1. Force resolved theme to 'dark' regardless of stored preference.
//   2. No-op setPreference / toggle (kept in API surface to avoid breaking imports).
// All infra (CSS vars, Tailwind plugin, admin Color Theme panel) stays dormant
// and ready to flip back on once Phase 1 ships.
// ─────────────────────────────────────────────────────────────────────────────
const FORCED_THEME = 'dark';

export function ThemeProvider({ children }) {
  // Apply forced dark theme to <html data-theme="dark">
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', FORCED_THEME);
  }, []);

  const setPreference = useCallback(() => {
    // No-op while warm mode is paused. Kept for API stability.
  }, []);

  const toggle = useCallback(() => {
    // No-op while warm mode is paused.
  }, []);

  return (
    <ThemeContext.Provider value={{ preference: FORCED_THEME, resolved: FORCED_THEME, setPreference, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
